import { Instant, LocalDate } from "@js-joda/core";
import * as messages from "../../messages";
import { AgencyCaregiverCertifications, AgencyCaregiverHireDate } from "../../messages";
import { CaregiverStatus } from "../../messages/CaregiverStatus";
import { CaregiverCertification, CaregiverId } from "../../messages/ObjectIds";
import { genericUpdate } from "../../utils/generic_update";

export interface AppCaregiverState {
  readonly caregiver: AppCaregiver;
}

export interface AppCaregiver {
  readonly id: CaregiverId;
  readonly status: CaregiverStatus;
  readonly caregiverCode: string | null;
  readonly createdAt: Instant;
  readonly birthDateDate: LocalDate | null;
  readonly phoneNumbers: messages.CaregiverPhoneNumber[];
  readonly homePhone: string | null;
  readonly firstName: string;
  readonly middleName: string | null;
  readonly lastName: string;
  readonly gender: string;
  readonly languages: string[];
  readonly address: string;
  readonly addressGeoLocation: messages.Geo;
  readonly certifications: CaregiverCertification[];
  readonly agenciesCaregiverCertifications: AgencyCaregiverCertifications[];
  readonly agenciesCaregiverHireDates: AgencyCaregiverHireDate[];
  readonly isCaregiverOfMedFlyt: boolean;
  readonly isInstapayVisible: boolean;
  readonly isInstapayAvailable: boolean;
  readonly shouldShowMyAgencies: boolean;
  readonly shouldShowCaregiverRating: boolean;
  readonly shouldShowNewChat: boolean;
  readonly shouldShowVirtualIdCard: boolean;
  readonly shouldShowComplianceBanner: boolean;
  readonly shouldShowMainV2Screen: boolean;
  readonly photoUrl: string | null;
  readonly installedUserAgent: string | null;
  readonly appInstalled: boolean;
  readonly isFullTime: boolean;
  readonly canChatWithOtherAgencies: boolean;
  readonly shouldShowMyMedflyCompliance: boolean;
  readonly shouldShowMyStaffingPreferences: boolean;
  readonly shouldShowCareMoments: boolean;
  readonly shouldShowRaffle: boolean;
  readonly points: number;
  readonly raffle: {
    shiftsPerToken: number;
    shiftsToNextToken: number;
    tokens: number;
    prize: string;
    daysLeft: number;
    progressPercentage: number;
  } | null;
}

export class AppCaregiverState {
  static fromMessage = (message: messages.CaregiverMobile): AppCaregiverState => {
    return {
      caregiver: {
        id: message.id,
        status: message.status,
        caregiverCode: message.caregiverCode,
        createdAt: message.createdAt,
        birthDateDate: message.birthDateDate,
        phoneNumbers: message.phoneNumbers,
        homePhone: message.homePhone,
        firstName: message.firstName,
        middleName: message.middleName,
        lastName: message.lastName,
        gender: message.gender,
        languages: message.languages,
        address: message.address,
        addressGeoLocation: message.addressGeoLocation,
        certifications: message.certifications,
        agenciesCaregiverCertifications: message.agenciesCaregiverCertifications,
        agenciesCaregiverHireDates: message.agenciesCaregiverHireDates,
        isCaregiverOfMedFlyt: message.isCaregiverOfMedFlyt,
        isInstapayVisible: message.isInstapayVisible,
        isInstapayAvailable: message.isInstapayAvailable,
        shouldShowMyAgencies: message.shouldShowMyAgencies,
        shouldShowCaregiverRating: message.shouldShowCaregiverRating,
        shouldShowNewChat: message.shouldShowNewChat,
        shouldShowVirtualIdCard: message.shouldShowVirtualIdCard,
        shouldShowComplianceBanner: message.shouldShowComplianceBanner,
        shouldShowMyStaffingPreferences: message.shouldShowMyStaffingPreferences,
        shouldShowCareMoments: message.shouldShowCareMoments,
        shouldShowRaffle: message.shouldShowRaffle,
        shouldShowMainV2Screen: message.shouldShowMainV2Screen,
        photoUrl: message.photoUrl,
        installedUserAgent: message.installedUserAgent,
        appInstalled: message.appInstalled,
        isFullTime: message.isFullTime,
        canChatWithOtherAgencies: message.canChatWithOtherAgencies,
        shouldShowMyMedflyCompliance: message.shouldShowMyMedflyCompliance,
        points: message.points,
        raffle: message.raffle,
      },
    };
  };

  static updateFromMessage = (
    appCaregiverState: AppCaregiverState,
    message: messages.CaregiverMobile
  ): AppCaregiverState => {
    return genericUpdate(appCaregiverState, AppCaregiverState.fromMessage(message));
  };
}
